import querystring from 'querystring';

import { GridSortDirection } from '@mui/x-data-grid';
import {
  createApi,
  FetchArgs,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import { Method } from 'axios';

import {
  checkAndGetNewAccessToken,
  getCachedAccessToken,
} from './checkAndGetNewAccessToken';
import {
  businessDetailsEntity,
  businessEntity,
  NumbersEntity,
  timezonesEntity,
} from './kixie-db-types-generated';

export const queryStringObject = (props: Record<string, any>) => {
  return `?${querystring.stringify(props)}`;
};

export interface SMSTemplateOption {
  businessid: number | null;
  hasCustomField: 0 | 1;
  isactive: 0 | 1;
  order: number | null;
  smsTemplateMessage: string;
  smsTemplateName: string;
  smsTemplateid: number;
  userid: number;
}

export interface AlertCenterItem {
  errormessage: string;
  code: string;
  crmid: number;
}

export interface IBusinessQueueTeams {
  teamId: number | string;
  teamName: string;
}

export interface IBusinessQueueMembers {
  canReceiveCalls: 0 | 1;
  available: 0 | 1;
  agentAvailable: 0 | 1;
  queueid: number;
  userid: number;
  subscribed: 0 | 1;
  DND: 0 | 1;
  onCall: 0 | 1;
  businessActive: 0 | 1;
  userActive: 0 | 1;
  sipActive: 0 | 1;
  powercallActive: 0 | 1;
  agentReserved: 0 | 1;
  teamActive: 0 | 1;
  agentName: string;
  teams: string | IBusinessQueueTeams[];
  ringtimeout: number;
  businessid: number;
  apiKey: string;
  localPresenceLite: 0 | 1;
  localPresenceOn: 0 | 1;
  localPresenceProgressive: 0 | 1;
  callerid: string;
  countrycode: string;
}

export interface IDirectory {
  DND: 0 | 1;
  businessid: number; // 995
  callStart: string | null; // null
  canBeManaged: 0 | 1;
  canCallCoach: 0 | 1;
  confName: string | null;
  crmlink: string | null;
  customerName: string | null;
  customerNumber: string | null;
  data: string | null;
  direction: 'incoming' | 'outgoing';
  ext: string; // "108",
  externalid: string; // "81eaf33a-3105-11ec-a61b-371b91862117",
  loggedin: 0 | 1;
  name: string; // "Jeff Kuei",
  oncall: 0 | 1;
  userid: number; // 1599,
  newuser?: 0 | 1;
}

export type CRM = {
  accountid: string | number;
  crmID: number;
  crmName: string;
  isActive: 0 | 1;
  location: string;
  logo: string;
  pass: string;
  token: string;
  user: string;
  userAgent: string;
  errordate: string;
  bcrmID?: number | string | null;
  businessid?: number | string | null;
  buser?: number | string | null;
  blocation?: number | string | null;
  bisActive?: number | string | null;
  ucrmID?: number;
  userid?: number;
};

export interface IQueryProps {
  userSettings:
    | {
        apikey: string;
        businessId: number;
        userid: number | string;
        timezone?: string;
      }
    | undefined;
  refetch?: string;
  extension?: string;
}

export interface ISMSMessage {
  Number: string;
  calendarTime: string;
  crmlink: string;
  direction: string;
  message: string;
  msgStatus: string;
  name: string;
  pcActions: string;
  phpcalldate2: string;
  phpcallstring: string;
  businessSMSId?: null | number;
  inboxNumber?: string;
  inboxName?: string;
  contactFirstName?: string;
  contactLastName?: string;
  active?: number;
  messagestate: string | null;
}

type TPrimitive = number | boolean | string | null;

export interface IBusinessModel {
  CallRecord: number;
  FSaccountid: string;
  FollowUpSMSActive: number;
  LocalPresenceType: string;
  PlivoAuth: string | null;
  SMSactive: number;
  SubscriptionSMSActive: number;
  accountManagerEmail: string | null;
  address1: string;
  allowAgentPowerlist: number;
  apiKey: string;
  apikeyenabled: number;
  autoAnswerApi: number;
  balanceupdated: string;
  billDay: number;
  blockOutboundCallRecordingSetting: string;
  blocked: TPrimitive;
  businessBillingTerm: number;
  businessContractTerm: string;
  businessURL: string;
  businesscountrycode: string;
  businessid: number;
  canDeleteUsers: number;
  category: string;
  city: string;
  clientid: number;
  connectionDuration: number;
  createContact: number;
  created: string | null;
  cred: string;
  crmID: string | null;
  directSync: number;
  discountPercent: number;
  email: string;
  emailHash: string;
  emailVerified: number;
  env: string;
  externalid: TPrimitive;
  frequency: string;
  fromGMT: number;
  hasCRM: number;
  hasSlack: number;
  hasWebhook: number;
  hashCT: string | null;
  holdMusic: string | null;
  inboundRingtone: string | null;
  invoicedUntil: string;
  isactive: number;
  lastcallid: string;
  lastcharge: string;
  lastdataupdate: string;
  lastinvoicedate: string | null;
  lastrechargeinvoice: string;
  leaderboardSort: string;
  logPowercallAttemptsToCrm: number;
  maxrate: number;
  minimumBalance: number;
  minutesBalance: number;
  name: string;
  onlyManagersCanAddPhoneNumbers: number;
  onlyManagersCanViewCallReporting: number;
  plivoRegion: string;
  plivoToken: string;
  plivoacctid: string;
  prepaidTrial: number;
  publicKey: string;
  rechargeAmount: number;
  recordingRetention: number;
  reserveBalance: number;
  salesRepEmail: string;
  salescycle: number;
  salescycletype: string;
  sendRecordingToCRM: number;
  setupRepEmail: string | null;
  sipRealm: string;
  sipUser: string;
  state: string;
  terminatingnumber: TPrimitive;
  // eslint-disable-next-line camelcase
  timezone_php: string;
  timezoneid: number;
  userTier: string;
  webhookDelay: number;
  website: string;
  defaultCrmUserid: string;
  DncBlocksOutboundSMS: number;
  stopBlocksOutboundCalls: number;
  requireOutcomeForAllAgents?: number;
  showOutcomeForAllAgents?: number;
}

export interface spProductPricingEntity {
  billDay: number | null;
  businessid?: number;
  discountedPrice: number | null;
  discountPercentage: number | null;
  displayName: string | null;
  fullPrice?: number;
  product: string | null;
  productBillingCycle: number | null;
  productid?: number;
  /** uniqueByUserId/uniqueByBusinessId means only one active instance of a product family per column type otherwise multiple instance allowed */
  productUniqueType: string | null;
  proratedDuration: number | null;
  proratedPrice: number | null;
}

export interface ICall {
  duration: number;
  Number: string; // '+15552523111';
  calendarTime: string; // 'Yesterday at 11:51 PM';
  calleridName: string; // 'Nang Chan';
  callstatus: string; // 'answered';
  calltype: 'incoming' | 'outgoing';
  crmlink: string; // 'https://app.hubspot.com/contacts/6283166/contact/1079001/';
  destinationName: string; // 'Direct Dial';
  name: string; // 'Chan Nang';
  pcActions: string; // '<span class=\'callhistory-name\'>Chan Nang</span><span class=\'historyc2c link pointer\'>+18052523111</span><span class=\'historyactionlinks\'><span><i  title="Answered Incoming" class="material-icons  green-text font-green-meadow" style="vertical-align:middle;">call_received</i></span><span><i title="&#x3C;div&#x3E;&#x3C;table class=&#x22;table  compact &#x22;&#x3E;&#x3C;tr&#x3E;&#x3C;td&#x3E;Result&#x3C;/td&#x3E;&#x3C;td&#x3E;incoming answered&#x3C;/td&#x3E;&#x3C;/tr&#x3E;&#x3C;tr&#x3E;&#x3C;td&#x3E;Date&#x3C;/td&#x3E;&#x3C;td&#x3E;10/21 11:51 pm&#x3C;/td&#x3E;&#x3C;/tr&#x3E;&#x3C;tr&#x3E;&#x3C;td&#x3E;To&#x3C;/td&#x3E;&#x3C;td&#x3E;12099263850&#x3C;/td&#x3E;&#x3C;/tr&#x3E;&#x3C;tr&#x3E;&#x3C;td&#x3E;From&#x3C;/td&#x3E;&#x3C;td&#x3E;18052523111&#x3C;/td&#x3E;&#x3C;/tr&#x3E;&#x3C;/table&#x3E;&#x3C;audio controls id=&#x22;1634885485-audio&#x22; class=&#x22;callrecordingcontrols&#x22; src=&#x22;https://calls.kixie.com/78b1f3da-3304-11ec-b559-e9e6a4cab423.mp3 &#x22; type=&#x22;audio/mpeg&#x22;&#x3E;&#x3C;/audio&#x3E;&#x3C;/div&#x3E;" class="material-icons blue-text font-blue callInfoTooltip pointer" style="vertical-align:middle;">info_outline</i></span><span class="inline-sms pointer" data-number="+18052523111"><i title="respond via SMS" class="material-icons font-blue" style="vertical-align:middle;">message</i></span><span><i id="1634885485-icon" title="Play Recording" class="material-icons orange-text font-yellow-gold playRecording pointer" style="vertical-align:middle;">play_circle_outline</i> <audio id="1634885485-audio" class="callrecording"><source src="https://calls.kixie.com/78b1f3da-3304-11ec-b559-e9e6a4cab423.mp3" type="audio/mpeg">Your browser does not support the audio element.</audio></span><span><a href="https://app.hubspot.com/contacts/6283166/contact/1079001/" target="_blank"><i title="link to crm" class="material-icons green-text font-green-meadow" style="vertical-align:middle;">link</i></a></span></span>';
  phpcalldate2: string; // "<span title='11:51 pm'>10/21<br>11:51 pm</span>";
  phpcallstring: string; // '1634885485000';
  recording: string; // '<audio id="1634885485-audio" class="callrecording"><source src="https://calls.kixie.com/78b1f3da-3304-11ec-b559-e9e6a4cab423.mp3" type="audio/mpeg">Your browser does not support the audio element.</audio>';
  recordingControls: string; // '<audio controls id="1634885485-audio" class="callrecordingcontrols" src="https://calls.kixie.com/78b1f3da-3304-11ec-b559-e9e6a4cab423.mp3 " type="audio/mpeg"></audio>';
  recordingurl: string; // 'https://calls.kixie.com/78b1f3da-3304-11ec-b559-e9e6a4cab423.mp3';
}

interface ICallTypes {
  createdAt: number;
  createdByUserId: 0 | 1;
  name: string;
  ordering: number;
  portalId: number;
}

export interface IVoicemail {
  Number: string;
  calendarTime: string;
  crmlink: string;
  messagemp3: string;
  name: string;
  pcActions: string;
  phpcalldate2: string;
  phpcallstring: string;
  recordingControls: string;
  voicemailid: number;
  externalcallid?: string;
}

interface IDispositionsList {
  result: [
    {
      dispositionArr: string[]; // 'Connected',
      dispositionString: string; // 'Connected,Busy,Won Deal,Left live message,Left voicemail,No answer,Wrong number,Test # 140603,Custom Test,belfort,Blocked by Gatekeeper,muenzetest22,testing,pauls new dos[po;lkjas;lkjsdvlkj,so i guess this works now,ghtest,send email for demo,MJcad,Webhook,Not Interested,name # 167655,name # 167656,hello again 3,Disposition 53,MJ call type,hello again 5 # 167910,hello again 5 # 167909,doge,TEST # 152952,Test # 167597,Disposition 081121,Disposition 081221,kixie absolutely loves pipedrive';
      // dispoId: string;
      dispoRes: {
        disposition: string;
        externalid: string;
        businessDispositionid: number;
      }[];
    }
  ];
  success: boolean;
}

interface ISmsTemplate {
  businessid: number | null;
  hasCustomField: 0 | 1;
  smsTemplateMessage: string;
  smsTemplateName: string;
  smsTemplateid: number;
  userid: number;
}

type TDirectoryFieldName =
  | 'callStart'
  | 'canBeManaged'
  | 'canCallCoach'
  | 'customerName'
  | 'customerNumber'
  | 'DND'
  | 'loggedin'
  | 'oncall';

interface IDirectoryValues<
  TBoolTypes extends TDirectoryFieldName =
    | 'callStart'
    | 'canBeManaged'
    | 'canCallCoach'
    | 'DND'
    | 'loggedin'
    | 'oncall'
> {
  values:
    | {
        name: TBoolTypes;
        value: 0 | 1;
      }
    | {
        name: Exclude<TDirectoryFieldName, TBoolTypes>;
        value: string;
      };
}

interface IInstalledVersion {
  values: {
    domain: string;
    platform: string;
    chromeVersion: number;
    version: string;
  };
}

export interface IQueueDirectory {
  DND: number;
  agentAvailable: number;
  agentName: string;
  agentQueueEnabled: number;
  agentReserved: number;
  apiKey: string;
  businessActive: number;
  businessid: number;
  callerid: string;
  canReceiveCalls: number;
  countrycode: string;
  id: number;
  isAgentQueue: number;
  localPresenceLite: number;
  localPresenceOn: number;
  localPresenceProgressive: number;
  onCall: number;
  powercallActive: number;
  queueid: number;
  ringtimeout: number;
  sipActive: number;
  subscribed: number;
  teamActive: number;
  userActive: number;
  oncall: number;
  userid: number;
  teams: string | [];
  queuename: string;
}

export interface IDirectoryWithAvailable extends IDirectory {
  available: 0 | 1;
}
export interface IQueueDirectoryWithAvailable extends IQueueDirectory {
  available: 0 | 1;
}

export function getDirectoryAvailable(
  entry: IDirectory
): IDirectoryWithAvailable {
  return {
    ...entry,
    available: !entry.DND && !entry.oncall && entry.loggedin ? 1 : 0,
  };
}
export function getQueueDirectoryAvailable(
  entry: IQueueDirectory
): IQueueDirectoryWithAvailable {
  return {
    ...entry,
    available: !entry.DND && !entry.oncall ? 1 : 0,
  };
}
export interface IDirectoryResponse {
  success: boolean;
  result: IDirectoryWithAvailable[];
}

export interface ICRMs3Response {
  result: CRM[];
  success: boolean;
}
export interface IQueueSubscription {
  result: [
    {
      queueid: number;
      subscribed: 0 | 1 | undefined;
      teamid: number;
      userid: number;
    }
  ];
  success: boolean;
}
export interface IAudioSettings {
  result: [
    {
      webrtcAudio: string | undefined;
      webrtcAudioLabel: string | undefined;
      webrtcMic: string | undefined;
      webrtcMicLabel: string | undefined;
      webrtcRing: string | undefined;
      webrtcRingLabel: string | undefined;
      audioDeviceGain: 0 | 1 | undefined;
      audioDeviceEcho: 0 | 1 | undefined;
      audioDeviceNoise: 0 | 1 | undefined;
    }
  ];
  success: boolean;
}

export interface ISipCreds {
  result: [
    {
      apiKey: string;
      businessid: number;
      cred: string;
      endpointid: string;
      preferredServer: string;
      sipRealm: string;
      sipUser: string;
      sipUserId: number;
      userid: number;
    }
  ];
  success: boolean;
}

// 10DLC types
export interface IBusinessCampaign {
  plivoCampaignId?: string;
  plivoBrandId?: string;
  campaignStatus?: string;
  Numbers?: string; // JSON string of numbers
  plivoReturnJson?: string; // JSON string
  subuseCse?: string;
  useCse?: string;
  mainTemplate?: number;
  kixieCampaignId?: string;
  brandId?: string;
  createddate?: string;
}

export interface IBusinessProfileBrand {
  brandId?: string;
  brandRegistrationStatus?: string;
  kixieBrandFormJson?: string;
  kixieProfileFormJson?: string;
  plivoBrandJson?: string;
  plivoProfileJson?: string;
  createddate?: string;
}
export interface IAudioDevice2 {
  result: [
    {
      varuserid: number;
      vardeviceid: string;
      varlabel: string;
    }
  ];
  success: boolean;
}
export interface IInsertEventResponse {
  success: boolean;
  data: {
    businessid: string;
    userid: number;
    eventtype: string;
    created: string;
  };
}
export interface IAddEventResponse {
  success: boolean;
  data: {
    businessid: string;
    userid: number;
    eventtype: string;
    created: string;
  };
}
export interface ISMSTemplateOptionResponse {
  success: boolean;
  result: SMSTemplateOption[];
}
export interface IBulkChangesEntity {
  action: 'upgrade' | 'downgrade' | 'add' | 'cancel' | 'unchanged';
  businessId?: number;
  discountEndDate: Date;
  discountPercent: number;
  invoicedUntil: Date | null;
  newCanHaveDiscount: 0 | 1;
  newDisplayName: string | null;
  newPricePerUnit: number | null;
  newProduct: string | null;
  newProductId: number | null;
  newProductRank: number | null;
  nextInvoicedUntil: Date | null;
  oldBusinessProductId: number | null;
  oldCanHaveDiscount: number | null;
  oldDisplayName: string | null;
  oldPricePerUnit: number | null;
  oldProduct: string | null;
  oldProductId: number | null;
  oldProductRank: number | null;
  productBillingCycle: number;
  productFamily: string | null;
  productUniqueType: string | null;
  proratedDuration: number;
  proratedPrice: number;
  userId: number;
}
export interface IBulkChangesEntityResponse {
  content: IBulkChangesEntity[];
}

export interface IAddAioCallOutcomeResponse {
  success: boolean;
  message?: string;
  data?: Record<string, unknown>;
}

interface CrmCapabilities {
  logCall: 0 | 1;
  logSms: 0 | 1;
  addNote: 0 | 1;
  scheduleActivity: 0 | 1;
  editContact: 0 | 1;
  contactSync: 0 | 1;
  createContact: 0 | 1;
  logsDisposition: 0 | 1;
  customProperties: 0 | 1;
  dispostionSync: 0 | 1;
  canAssignOwner: 0 | 1;
}

interface ISuperAdminBusinessStats {
  success: boolean;
  data: {
    JSON: string;
    contact: number;
    blocked: number;
    crmStats: {
      crmid: number;
      collectedAt: string;
      esContactsCount: number;
      contactsCount: number;
      usersCount: number;
    }[];
    apiUsage: {
      usagePlanId: number;
      quotaLimit: number;
      quotaPeriod: string;
      usedRequests: number;
      remainingRequests: number;
      percentageUsed: number;
      createdDate: string;
    };
  };
  errorMessage: null | string;
}

interface IGetUsageReportByBusinessId {
  success: boolean;
  result: {
    hits: {
      total: number;
      hits: {
        _source: {
          businessid: number;
          usagePlanId: number;
          quotaLimit: number;
          quotaPeriod: string;
          usedRequests: number;
          remainingRequests: number;
          percentageUsed: number;
          createdDate: string;
          updatedDatetime: string;
        };
      }[];
    };
  };
}

/**
 * NOTE: [pagination-data] only returns when limit is specified and inLessThanNumberIdOptional is null
 */
export interface IGetBusinessNumbersReturn {
  success?: boolean;
  /** [pagination-data] getBusinessNumbers2 has pagination support and will have total count */
  count?: number;
  /** [pagination-data] table of page to Numberid */
  pagemarkers?: {
    /** absolute ordering of rows based on row_number(order by Numberid desc) */
    rowId: number;
    /** page number */
    page: number;
    /** used to identify first row of each page */
    Numberid: number;
  }[];
  /** [pagination-data] pagemap hash of page to Numberid */
  pagemap?: Record<string, number>;
  result?: (Omit<
    NumbersEntity,
    | 'attImageData'
    | 'tmobileImagedata'
    | 'verizonImageData'
    | 'icehook'
    | 'IPQS'
    | 'robokiller'
    | 'youmail'
  > & {
    id: number;
    attImageData: string | null;
    tmobileImageData: string | null;
    verizonImageData: string | null;
    attImageDataObject: Record<string, any> & { status: 'spam' | 'spam' };
    tmobileImageDataObject: Record<string, any> & { status: 'spam' | 'spam' };
    verizonImageDataObject: Record<string, any> & { status: 'spam' | 'spam' };
    attImageDataStatus: string;
    tmobileImageDataStatus: string;
    verizonImageDataStatus: string;
    icehook: string;
    IPQS: string;
    robokiller: string;
    youmail: string;
    type:
      | 'Agent'
      | 'Conference'
      | 'Group'
      | 'Queue'
      | 'IVR'
      | 'Boost Set'
      | 'Directory'
      | 'unallocated';
    destinationName: string;
    destinationid: number | '';
  })[];
}

const [version] = `${
  window.chrome?.runtime?.getManifest
    ? window.chrome?.runtime?.getManifest()?.version
    : ['base']
}`;

function getQueryBody(
  props: IQueryProps,
  endpoint: string,
  payload: any,
  method?: Method
): string | FetchArgs {
  // Special case for omnidashboard - it's deployed to /utils/ not /itn/
  const baseUrl = endpoint === 'omnidashboard' ? '/utils/' : '/itn/';

  return {
    url: baseUrl + endpoint,
    method: method ?? 'POST',
    headers: {
      'x-api-key': props.userSettings?.apikey,
      'x-ver': version,
      'content-type': 'application/json;charset=utf-8',
    },
    body: {
      userId: props.userSettings?.userid,
      userid: props.userSettings?.userid,
      businessid: props.userSettings?.businessId,
      businessId: props.userSettings?.businessId,
      apikey: props.userSettings?.apikey,
      apiKey: props.userSettings?.apikey,
      refetch: props.refetch,
      ...payload,
    },
  };
}

function getQueryParams(
  props: IQueryProps,
  endpoint: string,
  params?: any,
  method?: Method
): string | FetchArgs {
  return {
    url: '/itn/' + endpoint + (params ?? ''),
    method: method ?? 'GET',
    headers: {
      'x-api-key': props.userSettings?.apikey,
      'x-ver': version,
      'content-type': 'application/json;charset=utf-8',
    },
  };
}

function formatSpam(obj?: { status: 'spam' | 'scam' }) {
  return !obj
    ? ''
    : obj?.status === 'spam' || obj?.status === 'scam'
    ? 'Spam'
    : 'Not Spam';
}

/**
 * Define a service using a base URL and expected endpoints
 * NOTE: cannot use nested arguments as input into query
 * @see https://redux-toolkit.js.org/rtk-query/usage/queries
 */
export const apigQuery = createApi({
  reducerPath: 'apigQuery',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.VITE_APP_API_DOMAIN,
    prepareHeaders: async (headers, { getState }) => {
      // check if accessToken not expired else retrieve new access token and make request
      if (!headers.has('authorization')) {
        const {
          accessToken: newAccessToken,
        } = await checkAndGetNewAccessToken();
        if (newAccessToken) {
          headers.set('authorization', newAccessToken);
        }
      }
      return headers;
    },
  }),
  // baseQuery: baseQueryWithDisabled,
  tagTypes: [
    'dispositions',
    'calltypes',
    'directory',
    'callHistory',
    'directoryTeams',
    'businessGroups',
    'vmDropOptions',
    'smsMessages',
    'voicemails',
    'endpoints',
    'getBusinessData',
    'getAllBusinessProducts',
    'getAllBusinessProductsFromDID',
    'getBusinessTranches',
    'queueDirectory',
    'queueHoldTimeById',
    'queueMembers',
    'CRMs3',
    'newUsertoOne',
    'queuesByUserid',
    'audioSettings',
    'sipCreds',
    'audioDevice',
    'sipRegistration',
    'insertevent',
    'addEvent',
    'deleteVoicemail',
    'updateDevice2',
    'getAgentSeatCountNumberCredit',
    'subscribeToAutoQueues',
    'plivoEnv',
    'updatePlivoEnv',
    'fromExtension',
    'user',
    'userCrm',
    'defaultCRM',
    'removeUserCRM',
    'updateQueueSubscription',
    'updateC2Con',
    'updatePopon',
    'updatePresenceon',
    'updateStyleMode',
    'updateCompactMode',
    'defaultDevice',
    'userSettings3',
    'sendsms',
    'threadedsms',
    'businessSmsThread',
    'featureFlags',
    'updateQuietMode',
    'getBusinessNumbers2',
    'crmCapabilities',
    'displayNpsForm',
  ],
  endpoints: (builder) => ({
    getCallTypes: builder.query<ICallTypes[], IQueryProps>({
      query: (props) =>
        getQueryBody(props, 'hubspot/calltypes', {
          call: 'getCallTypeList',
          refetch: props.refetch,
        }),
      providesTags: ['calltypes'],
    }),
    getBusiness: builder.query<
      {
        result: IBusinessModel[];
        success: boolean;
      },
      IQueryProps
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/getbusinessdata', {
          call: 'getbusinessdata',
        }),
      providesTags: ['getBusinessData'],
    }),
    getAllBusinessProducts: builder.query<
      spProductPricingEntity[],
      IQueryProps
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/getAllBusinessProducts', {
          call: 'getAllBusinessProducts',
        }),
      providesTags: ['getAllBusinessProducts'],
    }),
    getAllBusinessProductsFromDID: builder.query<
      {
        pricingData: spProductPricingEntity[];
        description: string;
      },
      IQueryProps & { did: string | number }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/getAllBusinessProductsFromDID', {
          call: 'getAllBusinessProductsFromDID',
          did: props.did,
        }),
      providesTags: ['getAllBusinessProductsFromDID'],
    }),
    getBusinessTranches: builder.query<
      {
        trancheData: {
          localPresenceTrancheId: string;
          created: string;
          businessproductid: number;
        }[];
      },
      IQueryProps
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/getBusinessTranches', {
          call: 'getBusinessTranches',
        }),
      providesTags: ['getBusinessTranches'],
    }),
    getAgentSeatCountNumberCredit: builder.query<
      {
        result: IBusinessModel[];
        success: boolean;
      },
      IQueryProps
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/getAgentSeatCountNumberCredit', {
          call: 'getAgentSeatCountNumberCredit',
        }),
      providesTags: ['getAgentSeatCountNumberCredit'],
    }),
    getDispositions: builder.query<
      { disposition: string; externalid: string }[],
      IQueryProps
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/getDispositionsList', {
          call: 'getDispositionsList',
        }),
      providesTags: ['dispositions'],
      transformResponse: (dispositions: IDispositionsList) =>
        dispositions?.result[0].dispoRes.map((disposition) => ({
          disposition: disposition.disposition,
          externalid: disposition.externalid,
          businessDispositionid: disposition.businessDispositionid,
        })) ?? [],
    }),
    getDirectory: builder.query<IDirectoryWithAvailable[], IQueryProps>({
      query: (props) =>
        getQueryBody(props, 'legacy/getUserDirectoryByBusiness', {
          call: 'getUserDirectoryByBusiness',
        }),
      providesTags: ['directory'],
      transformResponse: (response: { result?: IDirectory[] }) =>
        response?.result
          ?.sort((first, second) =>
            first.name?.toLowerCase().localeCompare(second.name?.toLowerCase())
          )
          .map((entry) => getDirectoryAvailable(entry)) ?? [],
    }),
    getUserDirectorySubscriptionData: builder.query<
      IDirectoryWithAvailable[],
      IQueryProps & {
        startdate: string;
        enddate: string;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/getUserDirectorySubscriptionData', {
          call: 'getUserDirectorySubscriptionData',
          startdate: props.startdate,
          enddate: props.enddate,
        }),
      providesTags: ['directory'],
    }),
    getUserDirectorySubscriptionDataByUserId: builder.mutation<
      Record<string, any>,
      IQueryProps & {
        startdate: string;
        enddate: string;
        userid: number;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/getUserDirectorySubscriptionDataByUserId', {
          call: 'getUserDirectorySubscriptionDataByUserId',
          startdate: props.startdate,
          enddate: props.enddate,
          userid: props.userid,
        }),
    }),
    getBlockedNumberRules: builder.mutation<
      {
        body: {
          success: boolean;
          took: number;
          timed_out: boolean;
          hits: {
            hits: {
              _id: string;
              _index: string;
              _score: number;
              _source: {
                businessid: number;
                created: string;
                inboundcalls: boolean;
                inboundsms: boolean;
                number: string;
                outboundcalls: boolean;
                outboundsms: boolean;
                update: boolean;
              };
            }[];
            total: number;
            max_score: number;
          };
        };
        statusCode: number;
        isBase64Encoded: boolean;
        headers: {
          [header: string]: string;
        };
      },
      IQueryProps & {
        number: string;
      }
    >({
      query: (props) =>
        getQueryParams(
          props,
          `blockedNumbers/numbers?businessid=${props.userSettings?.businessId}&apikey=${props.userSettings?.apikey}&getByNumber=true&number=${props.number}&ftc=true`
        ),
    }),
    getFeatureFlags: builder.query<
      {
        [key: string]: string;
      },
      IQueryProps & {
        businessid: number | string;
        envType: string;
        pcVersion: string;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/getFeatureFlags', {
          call: 'getFeatureFlags',
          businessid: props.businessid,
          pcVersion: props.pcVersion,
          env: props.envType,
        }),
      providesTags: ['featureFlags'],
    }),
    getQueueDirectory: builder.query<
      IQueueDirectoryWithAvailable[],
      IQueryProps
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/getQueueDirectoryByBusiness', {
          call: 'getQueueDirectoryByBusiness',
        }),
      providesTags: ['queueDirectory'],
      transformResponse: (response: { result?: IQueueDirectory[] }) =>
        response?.result
          ?.sort((first, second) =>
            first.agentName
              ?.toLowerCase()
              .localeCompare(second.agentName?.toLowerCase())
          )
          .map((entry) => getQueueDirectoryAvailable(entry)) ?? [],
    }),
    getQueueHoldTimeById: builder.query<
      Record<string, any>,
      IQueryProps & {
        businessid: string | number;
        queueid: string | number;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/getQueueHoldTimeById', {
          call: 'getQueueHoldTimeById',
          businessid: props.businessid,
          queueid: props.queueid,
        }),
      providesTags: ['queueHoldTimeById'],
    }),
    getQueueMembers: builder.query<
      IBusinessQueueMembers,
      IQueryProps & { queueid: number }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/getQueueMembers', {
          call: 'getQueueMembers',
          businessid: props.userSettings?.businessId,
          queueid: props.queueid,
        }),
    }),
    loginDash: builder.mutation<
      Record<string, any>,
      {
        email: string;
        password: string;
      }
    >({
      query: (props) => ({
        url: '/itn/loginDash',
        method: 'POST',
        headers: {
          authorization: getCachedAccessToken(), // accessToken in recoil maybe stale
          'content-type': 'application/json;charset=utf-8',
        },
        body: {
          json: {
            email: props.email,
            password: props.password,
          },
        },
      }),
    }),
    updateDirectory: builder.mutation<
      Record<string, any>,
      IQueryProps & IDirectoryValues
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/updateUserDirectory', {
          call: 'updateUserDirectory',
          userid: props.userSettings?.userid,
          businessid: props.userSettings?.businessId,
          name: props.values.name, //this is the fieldname in the db
          value: props.values.value,
          apikey: props.userSettings?.apikey,
        }),
      invalidatesTags: ['directory'],
    }),
    getHistory: builder.query<
      {
        calls: ICall[];
        success: boolean;
        total: number;
      },
      IQueryProps & {
        timezone: string;
        gte: string;
        lte: string;
        start: number;
        length: number;
        search: string;
        useSearch: boolean;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'getUserCallHistory', {
          call: 'getHistory',
          timezone: props.timezone,
          gte: props.gte,
          lte: props.lte,
          start: props.start,
          length: props.length,
          search: props.search,
          useSearch: props.useSearch,
        }),
      providesTags: ['callHistory'],
    }),
    checkEmailAvailability: builder.query<
      { message: string },
      { email: string }
    >({
      query: (props) => ({
        url:
          '/itn/checkEmailAvailability?' +
          querystring.stringify({
            email: props.email,
          }),
        method: 'GET',
        headers: {
          authorization: getCachedAccessToken(), // accessToken in recoil maybe stale
          'content-type': 'application/json;charset=utf-8',
        },
      }),
    }),
    getDirectoryTeams: builder.query<Record<string, any>, IQueryProps>({
      query: (props) =>
        getQueryBody(props, 'getDirectoryTeams', {
          call: 'getDirectoryTeams',
        }),
      providesTags: ['directoryTeams'],
    }),
    getBusinessGroups: builder.query<Record<string, any>, IQueryProps>({
      query: (props) =>
        getQueryBody(props, 'legacy/getBusinessGroups', {
          call: 'getBusinessGroups',
        }),
      providesTags: ['businessGroups'],
    }),
    getVMDropOptions: builder.query<
      Record<string, any>,
      IQueryProps & {
        key: string;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'getvmdropOptions', {
          call: 'getvmdropOptions',
          key: props.key,
        }),
      providesTags: ['vmDropOptions'],
    }),
    getSMSHistory: builder.query<
      {
        messages: ISMSMessage[];
        success: boolean;
      },
      IQueryProps & {
        timezone: string;
        startrow: number;
        batchlength: number;
        search: string;
        useSearch: boolean;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'getusermessages', {
          call: 'getMessages',
          timezone: props.timezone,
          startrow: props.startrow,
          batchlength: props.batchlength,
          search: props.search,
          useSearch: props.useSearch,
        }),
      async onQueryStarted({ ...patch }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          apigQuery.util.updateQueryData('getSMSHistory', patch, (draft) => {
            Object.assign(draft, patch);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      providesTags: ['smsMessages'],
    }),
    getUserVoicemails: builder.query<
      {
        voicemail: IVoicemail[];
        success: boolean;
      },
      IQueryProps & {
        batchlength: number;
        lte: string;
        search: string;
        startrow: number;
        timezone: string;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'getuservoicemail', {
          call: 'getVoicemail',
          timezone: props.timezone,
          startrow: props.startrow,
          batchlength: props.batchlength,
          search: props.search,
          lte: props.lte,
        }),
      providesTags: ['voicemails'],
    }),
    getThreadedSMS: builder.query<
      Record<string, any>,
      IQueryProps & {
        number: string;
        timezone: string;
        startrow: number;
        batchlength: number;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'getthreadedsms', {
          call: 'getthreadedsms',
          number: props.number,
          timezone: props.timezone,
          useBatch: true,
          startrow: props.startrow,
          batchlength: props.batchlength,
        }),
      providesTags: ['threadedsms'],
    }),
    getBusinessSMSThread: builder.query<
      Record<string, any>,
      IQueryProps & {
        number: string | undefined;
        inboxNumber: string | undefined;
        businessSMSId: number | undefined;
        timezone: string | undefined;
        startrow: number | undefined;
        batchlength: number | undefined;
        auth: string | undefined;
      }
    >({
      query: (props) => ({
        url: `/app/businessSMS/messages${queryStringObject({
          businessid: props.userSettings?.businessId,
          businessId: props.userSettings?.businessId,
          userId: props.userSettings?.userid,
          userid: props.userSettings?.userid,
          apiKey: props.userSettings?.apikey,
          number: props.number,
          inboxNumber: props.inboxNumber,
          businessSMSId: props.businessSMSId,
          timezone: props.timezone,
          startrow: props.startrow,
          batchlength: props.batchlength,
          useBatch: true,
        })}`,
        method: 'GET',
        headers: {
          authorization: props.auth, // accessToken in recoil maybe stale
          'x-api-key': props.userSettings?.apikey,
          'x-ver': version,
          'content-type': 'application/json;charset=utf-8',
        },
      }),
      providesTags: ['businessSmsThread'],
    }),
    getMCCount: builder.query<
      {
        count: number;
        date: string;
      },
      IQueryProps
    >({
      query: (props) =>
        getQueryBody(props, 'getmccount', {
          call: 'getmccount',
        }),
    }),
    getVMCount: builder.query<
      {
        count: number;
        date: string;
      },
      IQueryProps
    >({
      query: (props) =>
        getQueryBody(props, 'getvmcount', {
          call: 'getvmcount',
        }),
    }),
    getSMSCount: builder.query<
      {
        count: number;
        date: string;
      },
      IQueryProps
    >({
      query: (props) =>
        getQueryBody(props, 'getsmscount', {
          call: 'getsmscount',
        }),
    }),
    getSMSTemplate: builder.query<{ result: ISmsTemplate[] }, IQueryProps>({
      query: (props) =>
        getQueryBody(props, 'legacy/getsmstemplate', {
          call: 'getsmstemplate',
        }),
    }),
    getUserNumbersAndEndpoints: builder.query<Record<string, any>, IQueryProps>(
      {
        query: (props) =>
          getQueryBody(props, 'legacy/getUserNumbersAndEndpoints', {
            call: 'getUserNumbersAndEndpoints',
            userid: props.userSettings?.userid,
          }),
        providesTags: ['endpoints'],
      }
    ),
    updateInstalledVersion: builder.mutation<
      Record<string, any>,
      IQueryProps & IInstalledVersion
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/updateInstalledVersion', {
          call: 'updateInstalledVersion',
          userid: props.userSettings?.userid,
          businessid: props.userSettings?.businessId,
          apikey: props.userSettings?.apikey,
          domain: props.values.domain, //this is the fieldname in the db
          platform: props.values.platform,
          chromeVersion: props.values.chromeVersion,
          version: props.values.version,
        }),
    }),
    updateEndpoint: builder.mutation<
      {
        success: boolean;
      },
      IQueryProps & {
        newEnv: string;
        endpointId: string;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/updateEndpoint', {
          call: 'updateEndpoint',
          businessid: props.userSettings?.businessId,
          apikey: props.userSettings?.apikey,
          newEnv: props.newEnv,
          endpointId: props.endpointId,
        }),
      invalidatesTags: ['endpoints'],
    }),
    sendSMS: builder.mutation<
      {
        success: boolean;
      },
      IQueryProps & {
        number: string;
        message: string;
        env: string;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'sendsms', {
          call: 'sendsms',
          businessid: props.userSettings?.businessId,
          businessId: props.userSettings?.businessId,
          userid: props.userSettings?.userid,
          userId: props.userSettings?.userid,
          apiKey: props.userSettings?.apikey,
          number: props.number,
          message: props.message,
          env: props.env,
        }),
      invalidatesTags: ['sendsms'],
    }),
    updateNewUsertoOne: builder.mutation<
      {
        success: boolean;
        result: [
          {
            done: string;
          }
        ];
      },
      IQueryProps
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/updateNewUsertoOne', {
          call: 'updateNewUsertoOne',
          businessid: props.userSettings?.businessId,
          userid: props.userSettings?.userid,
          apikey: props.userSettings?.apikey,
        }),
      invalidatesTags: ['newUsertoOne'],
    }),
    unsubscribeFromAllQueuesByUserid: builder.mutation<
      IQueueSubscription,
      IQueryProps
    >({
      query: (props) =>
        getQueryBody(props, '/legacy/unsubscribeFromAllQueuesByUserid', {
          call: 'unsubscribeFromAllQueuesByUserid',
          businessid: props.userSettings?.businessId,
          userid: props.userSettings?.userid,
          apikey: props.userSettings?.apikey,
        }),
      invalidatesTags: ['queuesByUserid'],
    }),
    subscribeToAutoQueues: builder.mutation<IQueueSubscription, IQueryProps>({
      query: (props) =>
        getQueryBody(props, '/legacy/subscribeToAutoQueues', {
          call: 'subscribeToAutoQueues',
          businessid: props.userSettings?.businessId,
          userid: props.userSettings?.userid,
          apikey: props.userSettings?.apikey,
        }),
      invalidatesTags: ['subscribeToAutoQueues'],
    }),
    updateQueueSubscription: builder.mutation<
      IQueueSubscription,
      IQueryProps & {
        queueid: string;
        value: 0 | 1;
      }
    >({
      query: (props) =>
        getQueryBody(props, '/legacy/updateQueueSubscription', {
          call: 'updateQueueSubscription',
          userid: props.userSettings?.userid,
          apikey: props.userSettings?.apikey,
          queueid: props.queueid,
          value: props.value,
        }),
      invalidatesTags: ['updateQueueSubscription'],
    }),
    getSipCreds: builder.mutation<ISipCreds, IQueryProps>({
      query: (props) =>
        getQueryBody(props, '/legacy/getSipCreds', {
          call: 'getSipCreds',
          businessid: props.userSettings?.businessId,
          userid: props.userSettings?.userid,
          apikey: props.userSettings?.apikey,
        }),
      invalidatesTags: ['sipCreds'],
    }),
    checkSipRegistration: builder.mutation<
      Record<string, any>,
      IQueryProps & {
        sipuserid: string | number;
        endpointid: string | number;
      }
    >({
      query: (props) =>
        getQueryBody(props, '/legacy/checkSipRegistration', {
          call: 'checkSipRegistration',
          businessid: props.userSettings?.businessId,
          userid: props.userSettings?.userid,
          apikey: props.userSettings?.apikey,
          sipuserid: props.sipuserid,
          endpointid: props.endpointid,
        }),
      invalidatesTags: ['sipRegistration'],
    }),
    updateAudioDevice: builder.mutation<
      IAudioDevice2,
      IQueryProps & {
        device: string;
        label: string;
        type: string;
      }
    >({
      query: (props) =>
        getQueryBody(props, '/legacy/updateAudioDevice', {
          call: 'updateAudioDevice',
          businessid: props.userSettings?.businessId,
          userid: props.userSettings?.userid,
          apikey: props.userSettings?.apikey,
          device: props.device,
          label: props.label,
          type: props.type,
        }),
      invalidatesTags: ['audioDevice'],
    }),
    updateDevice2: builder.mutation<
      IAudioDevice2,
      IQueryProps & {
        device: string;
        label: string;
        type: string;
      }
    >({
      query: (props) =>
        getQueryBody(props, '/legacy/updateDevice2', {
          call: 'updateDevice2',
          businessid: props.userSettings?.businessId,
          userid: props.userSettings?.userid,
          apikey: props.userSettings?.apikey,
          device: props.device,
          label: props.label,
          type: props.type,
        }),
      invalidatesTags: ['updateDevice2'],
    }),
    deleteVoicemail: builder.mutation<
      Record<string, any>,
      IQueryProps & {
        vmid: number;
      }
    >({
      query: (props) =>
        getQueryBody(props, '/legacy/deleteVoicemail', {
          call: 'deleteVoicemail',
          businessid: props.userSettings?.businessId,
          userid: props.userSettings?.userid,
          apikey: props.userSettings?.apikey,
          vmid: props.vmid,
        }),
      invalidatesTags: ['deleteVoicemail'],
    }),
    insertEvent: builder.mutation<
      IInsertEventResponse,
      IQueryProps & {
        eventType: string;
        payload?: Record<string, any>;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'insertevent', {
          call: 'insertevent',
          businessid: props.userSettings?.businessId,
          userid: props.userSettings?.userid,
          apikey: props.userSettings?.apikey,
          eventType: props.eventType,
          payload: props.payload,
        }),
      invalidatesTags: ['insertevent'],
    }),
    addEvent: builder.mutation<
      IAddEventResponse,
      IQueryProps & {
        businessId: string | number;
        eventType: string;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/addEvent', {
          call: 'addEvent',
          businessid: props.businessId,
          userid: props.userSettings?.userid,
          apikey: props.userSettings?.apikey,
          event: props.eventType,
        }),
      invalidatesTags: ['addEvent'],
    }),
    getCRMs3: builder.mutation<ICRMs3Response, IQueryProps>({
      query: (props) =>
        getQueryBody(props, 'legacy/getCRMs3', {
          call: 'getCRMs3',
          businessid: props.userSettings?.businessId,
          userid: props.userSettings?.userid,
          apikey: props.userSettings?.apikey,
          apiKey: props.userSettings?.apikey,
        }),
      invalidatesTags: ['CRMs3'],
    }),
    getAudioSettings: builder.mutation<IAudioSettings, IQueryProps>({
      query: (props) =>
        getQueryBody(props, 'legacy/getAudioSettings', {
          call: 'getAudioSettings',
          businessid: props.userSettings?.businessId,
          userid: props.userSettings?.userid,
          apikey: props.userSettings?.apikey,
        }),
      invalidatesTags: ['audioSettings'],
    }),
    getUserDirectoryByBusiness: builder.mutation<
      IDirectoryResponse,
      IQueryProps
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/getUserDirectoryByBusiness', {
          call: 'getUserDirectoryByBusiness',
          businessid: props.userSettings?.businessId,
          businessId: props.userSettings?.businessId,
          userid: props.userSettings?.userid,
          apikey: props.userSettings?.apikey,
          apiKey: props.userSettings?.apikey,
        }),
    }),
    getPlivoEnv: builder.mutation<
      Record<string, any>,
      IQueryProps & { number: string }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/getPlivoEnv', {
          call: 'getPlivoEnv',
          businessid: 995,
          apikey: props.userSettings?.apikey,
          number: props.number,
        }),
      invalidatesTags: ['plivoEnv'],
    }),
    updatePlivoEnv: builder.mutation<
      Record<string, any>,
      IQueryProps & {
        number: string;
        newEnv: string;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/updatePlivoEnv', {
          call: 'updatePlivoEnv',
          businessid: 995,
          apikey: props.userSettings?.apikey,
          number: props.number,
          newEnv: props.newEnv,
        }),
      invalidatesTags: ['updatePlivoEnv'],
    }),
    sendToVmDrop: builder.mutation<
      Record<string, any>,
      IQueryProps & {
        device: string | undefined;
        vmdid: number | string;
        externalid: string;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'normalvmdrop', {
          call: 'sendtovmdrop',
          businessid: props.userSettings?.businessId,
          userid: props.userSettings?.userid,
          key: props.userSettings?.apikey,
          device: props.device,
          vmdid: props.vmdid,
          externalid: props.externalid,
        }),
    }),
    updateUser: builder.mutation<
      Record<string, any>,
      IQueryProps & {
        name: string;
        value: number;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/updateUser', {
          call: 'updateUser',
          businessid: Number(props.userSettings?.businessId),
          userid: Number(props.userSettings?.userid),
          apikey: props.userSettings?.apikey,
          name: props.name,
          value: props.value,
        }),
      invalidatesTags: ['user'],
    }),
    setAsDefaultCRM: builder.mutation<
      Record<string, any>,
      IQueryProps & {
        crmID: string;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/setAsDefaultCRM', {
          call: 'setAsDefaultCRM',
          businessid: props.userSettings?.businessId,
          userid: props.userSettings?.userid,
          apikey: props.userSettings?.apikey,
          crmID: props.crmID,
        }),
      invalidatesTags: ['defaultCRM'],
    }),
    removeUserCRM: builder.mutation<
      Record<string, any>,
      IQueryProps & {
        crmID: string;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/removeUserCRM', {
          call: 'removeUserCRM',
          businessid: props.userSettings?.businessId,
          userid: props.userSettings?.userid,
          apikey: props.userSettings?.apikey,
          crmID: props.crmID,
        }),
      invalidatesTags: ['removeUserCRM'],
    }),
    updateC2Con: builder.mutation<
      Record<string, any>,
      IQueryProps & {
        C2Con: Record<string, any>;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/updateC2Con', {
          call: 'updateC2Con',
          businessid: props.userSettings?.businessId,
          userid: Number(props.userSettings?.userid),
          apikey: props.userSettings?.apikey,
          C2Con: props.C2Con,
        }),
      invalidatesTags: ['updateC2Con'],
    }),
    updatePopon: builder.mutation<
      Record<string, any>,
      IQueryProps & {
        popon: Record<string, any>;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/updatePopon', {
          call: 'updatePopon',
          businessid: props.userSettings?.businessId,
          userid: Number(props.userSettings?.userid),
          apikey: props.userSettings?.apikey,
          popon: props.popon,
        }),
      invalidatesTags: ['updatePopon'],
    }),
    updatePresenceon: builder.mutation<
      Record<string, any>,
      IQueryProps & {
        popon: Record<string, any>;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/updatePresenceon', {
          call: 'updatePresenceon',
          businessid: props.userSettings?.businessId,
          userid: Number(props.userSettings?.userid),
          apikey: props.userSettings?.apikey,
          popon: props.popon,
        }),
      invalidatesTags: ['updatePresenceon'],
    }),
    updateStyleMode: builder.mutation<
      Record<string, any>,
      IQueryProps & {
        mode: Record<string, any>;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/updateStyleMode', {
          call: 'updateStyleMode',
          businessid: props.userSettings?.businessId,
          userid: Number(props.userSettings?.userid),
          apikey: props.userSettings?.apikey,
          mode: props.mode,
        }),
      invalidatesTags: ['updateStyleMode'],
    }),
    updateCompactMode: builder.mutation<
      Record<string, any>,
      IQueryProps & {
        mode: Record<string, any>;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/updateCompactMode', {
          call: 'updateCompactMode',
          businessid: props.userSettings?.businessId,
          userid: Number(props.userSettings?.userid),
          apikey: props.userSettings?.apikey,
          mode: props.mode,
        }),
      invalidatesTags: ['updateCompactMode'],
    }),
    updateQuietMode: builder.mutation<
      Record<string, any>,
      IQueryProps & {
        mode: Record<string, any>;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/updateQuietMode', {
          call: 'updateQuietMode',
          businessid: props.userSettings?.businessId,
          userid: Number(props.userSettings?.userid),
          apikey: props.userSettings?.apikey,
          mode: props.mode,
        }),
      invalidatesTags: ['updateQuietMode'],
    }),
    updateDefaultDevice: builder.mutation<
      Record<string, any>,
      IQueryProps & {
        device: Record<string, any>;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/updateDefaultDevice', {
          call: 'updateDefaultDevice',
          businessid: props.userSettings?.businessId,
          userid: Number(props.userSettings?.userid),
          apikey: props.userSettings?.apikey,
          device: props.device,
        }),
      invalidatesTags: ['defaultDevice'],
    }),
    updateUserSettings3: builder.mutation<
      Record<string, any>,
      IQueryProps & {
        calleridName: string | undefined;
        calleridNumber: number | undefined;
        smsnumber: number | undefined;
        firstName: string | undefined;
        lastName: string | undefined;
        countrycode: string | undefined;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/updateUserSettings3', {
          call: 'updateUserSettings3',
          businessid: props.userSettings?.businessId,
          userid: Number(props.userSettings?.userid),
          apikey: props.userSettings?.apikey,
          calleridName: props.calleridName,
          calleridNumber: props.calleridNumber,
          smsnumber: props.smsnumber,
          firstName: props.firstName,
          lastName: props.lastName,
          countrycode: props.countrycode,
        }),
      invalidatesTags: ['userSettings3'],
    }),
    getQueuesByuserid: builder.query<IQueueSubscription, IQueryProps>({
      query: (props) =>
        getQueryBody(props, 'legacy/getQueuesByuserid', {
          call: 'getQueuesByuserid',
          userid: props.userSettings?.userid,
          apikey: props.userSettings?.apikey,
        }),
    }),
    getAlertCenterList: builder.query<
      AlertCenterItem[],
      IQueryProps & { errorList: string }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/getAlertCenterList', {
          call: 'getAlertCenterList',
          userid: props.userSettings?.userid,
          errorList: props.errorList,
        }),
    }),
    updateAlertCenterList: builder.mutation<
      { data: { data: Record<string, any> } },
      IQueryProps & { errorCode: string }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/updateAlertCenterList', {
          call: 'updateAlertCenterList',
          userid: props.userSettings?.userid,
          errorCode: props.errorCode,
        }),
    }),
    getCRMReoauthStatus: builder.query<
      { data: { dismissedDate: string; count: number }[] },
      IQueryProps & { codeList: string; crmid: number | undefined }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/getCRMReoauthStatus', {
          call: 'getCRMReoauthStatus',
          userid: props.userSettings?.userid,
          crmid: props.crmid,
          errorCodes: props.codeList,
        }),
    }),
    setCRMReoauthStatus: builder.mutation<
      { data: { data: Record<string, any> } },
      IQueryProps & {
        codeList: string;
        crmid: number | undefined;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/setCRMReoauthStatus', {
          call: 'setCRMReoauthStatus',
          userid: props.userSettings?.userid,
          crmid: props.crmid,
          errorCodes: props.codeList,
        }),
    }),
    getLiveAndHeldCallsByUser: builder.query<
      { data: { data: Record<string, any> } },
      IQueryProps & {
        callUUID: string;
        retrieveLiveCalls?: boolean;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/getLiveAndHeldCallsByUser', {
          call: 'getLiveAndHeldCallsByUser',
          retrieveLiveCalls: props.retrieveLiveCalls ?? false,
          callUUID: props.callUUID ?? '',
          businessid: props.userSettings?.businessId,
          userid: props.userSettings?.userid,
        }),
    }),
    getSwitchUsersByFilter: builder.mutation<
      { data: { data: Record<string, any> } },
      IQueryProps & { filter: string; term: string; start: number }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/getSwitchUsersByFilter', {
          call: 'getSwitchUsersByFilter',
          filter: props.filter ?? '',
          term: props.term ?? '',
          start: props.start ?? 0,
          userid: props.userSettings?.userid,
        }),
    }),
    getSMSTemplateOptions: builder.query<
      ISMSTemplateOptionResponse,
      IQueryProps
    >({
      query: (props) =>
        getQueryBody(props, 'getsmstemplateoptions', {
          call: 'getHistory',
          businessid: props.userSettings?.businessId,
          userid: props.userSettings?.userid,
          key: props.userSettings?.apikey,
        }),
    }),
    getCallID: builder.query<
      Record<string, any>,
      IQueryProps & {
        to: string | undefined;
        from: string | undefined;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'getcallid', {
          call: 'getcallid',
          businessid: props.userSettings?.businessId,
          userid: props.userSettings?.userid,
          apiKey: props.userSettings?.apikey,
          from: props.from,
          to: props.to,
        }),
    }),
    c2c: builder.mutation<
      Record<string, any>,
      IQueryProps & {
        v: string | number;
        device: string | number;
        number: string | number;
        targetExt: string | number;
        key: string;
        env: string;
        externalcrmid: string | number;
        externalactivityid: string | number;
        domain: string | number;
      }
    >({
      query: (props) =>
        getQueryBody(
          props,
          'c2c?' +
            querystring.stringify({
              v: props.v,
              device: props.device,
              number: props.number,
              targetExt: props.targetExt,
              key: props.key,
              env: props.env,
              externalcrmid: props.externalcrmid,
              externalactivityid: props.externalactivityid,
              domain: props.domain,
            }),
          {}
        ),
    }),
    updateUserProductSettings: builder.mutation<
      Record<string, any>,
      IQueryProps & {
        userTier: string;
        ftcBlocking: number;
        localPresenceLite: number;
        userIds: number[];
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/updateUserProductSettings', {
          call: 'updateUserProductSettings',
          userTier: props.userTier,
          ftcBlocking: props.ftcBlocking,
          localPresenceLite: props.localPresenceLite,
          userIds: props.userIds,
        }),
    }),
    getActiveUserProducts: builder.query<
      Record<string, any>,
      IQueryProps & {
        userIds: number[];
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/getActiveUserProducts', {
          call: 'getActiveUserProducts',
          userIds: props.userIds,
        }),
    }),
    bulkChangeProducts: builder.mutation<
      IBulkChangesEntityResponse,
      IQueryProps & {
        preflight: number;
        content: {
          userId: number;
          product: string;
        }[];
      }
    >({
      query: (props) => {
        return {
          url:
            `/utils/bulkChangeProducts/${props?.userSettings?.businessId}?` +
            querystring.stringify({
              preflight: props.preflight,
              accessToken: 'no-auth',
            }),
          method: 'POST',
          headers: {
            authorization: getCachedAccessToken(),
            'x-api-key': props.userSettings?.apikey,
            'x-ver': version,
            'content-type': 'application/json;charset=utf-8',
          },
          body: {
            content: props.content,
          },
        };
      },
    }),
    updateAuditMetaData: builder.mutation<
      Record<string, any>,
      IQueryProps & {
        updatedByUserId: number;
        updateRows: Record<string, any>;
      }
    >({
      query: (props) => {
        return {
          url: '/itn/legacy/updateAuditMetaData',
          method: 'POST',
          headers: {
            authorization: getCachedAccessToken(), // accessToken in recoil maybe stale
            'x-api-key': props.userSettings?.apikey,
            'x-ver': version,
            'content-type': 'application/json;charset=utf-8',
          },
          body: {
            call: 'updateAuditMetaData',
            updatedByUserId: props.updatedByUserId,
            updateRows: props.updateRows,
          },
        };
      },
    }),
    getAuditMetaData: builder.query<
      Record<string, any>,
      IQueryProps & {
        tableName: string;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/getAuditMetaData', {
          call: 'getAuditMetaData',
          tableName: props.tableName,
        }),
    }),
    getBusinessNumbers: builder.query<
      IGetBusinessNumbersReturn,
      IQueryProps & {
        /** businessSMSId: string, name: stirng */
        smsIdLookup?: Record<string, string>;
        /** userid: string, name: string */
        usernameLookup?: Record<string, string>;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/getBusinessNumbers', {
          call: 'getBusinessNumbers',
        }),
      transformResponse(
        baseQueryReturnValue: IGetBusinessNumbersReturn | undefined,
        meta,
        arg
      ) {
        return {
          success: !!baseQueryReturnValue?.success,
          result: baseQueryReturnValue?.result?.map((n) => {
            // NOTE: please note that any changes here may break backend sorting. Apply any changes made here
            //       to the `getBusinessNumbers3` stored procedure, too.
            const row = {
              ...n,
              id: n.Numberid!,
              businessSMSName: arg.smsIdLookup?.[n.businessSMSId!] ?? '',
              attImageDataObject: n.attImageData
                ? JSON.parse(n.attImageData)
                : undefined,
              tmobileImageDataObject: n.tmobileImageData
                ? JSON.parse(n.tmobileImageData)
                : undefined,
              verizonImageDataObject: n.verizonImageData
                ? JSON.parse(n.verizonImageData)
                : undefined,
              destinationName:
                n.destinationName !== 'Boost Set'
                  ? n.destinationName
                  : n.localPresenceTrancheId
                  ? `Boost Set ${n.localPresenceTrancheId}`
                  : 'Local Presence',
              type: n.portStatus === 'externalDirectory' ? 'Directory' : n.type,
              defaultuserName:
                (n as any).defaultuserName ??
                arg.usernameLookup?.[n.defaultuser!] ??
                'N/A',
            };
            row.attImageDataStatus = formatSpam(row.attImageDataObject);
            row.tmobileImageDataStatus = formatSpam(row.tmobileImageDataObject);
            row.verizonImageDataStatus = formatSpam(row.verizonImageDataObject);
            return row;
          }),
        };
      },
    }),
    getBusinessNumbers2: builder.query<
      IGetBusinessNumbersReturn,
      IQueryProps & {
        /** default: 0 */
        skip?: number;
        /** default: Number.MAX_SAFE_INTEGER */
        limit?: number;
        /** order by Numberid desc if limit is not null and lessThanNumberId is null then return back pagination data */
        lessThanNumberId?: number;
        /** businessSMSId: string, name: stirng */
        smsIdLookup?: Record<string, string>;
        /** userid: string, name: string */
        usernameLookup?: Record<string, string>;
        /** filter over type, destinationName and destinationid */
        searchText?: string;
        /** sort by field */
        sort?: string;
        /** sortDirection by direction */
        sortDirection?: GridSortDirection;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/getBusinessNumbers2', {
          call: 'getBusinessNumbers2',
          skip: props.skip,
          limit: props.limit,
          lessThanNumberId: props.lessThanNumberId,
          searchText: props.searchText,
          sort: props.sort,
          sortDirection: props.sortDirection,
        }),
      transformResponse(
        baseQueryReturnValue: IGetBusinessNumbersReturn | undefined,
        meta,
        arg
      ) {
        return {
          success: !!baseQueryReturnValue?.success,
          count: baseQueryReturnValue?.count,
          pagemarkers: baseQueryReturnValue?.pagemarkers,
          pagemap: baseQueryReturnValue?.pagemarkers?.reduce((acc, item) => {
            acc[item.page] = item.Numberid;
            return acc;
          }, {} as Record</* page: */ string, /* Numberid: */ number>),
          result: baseQueryReturnValue?.result?.map((n) => {
            const row = {
              ...n,
              id: n.Numberid!,
              businessSMSName: arg.smsIdLookup?.[n.businessSMSId!] ?? '',
              attImageDataObject: n.attImageData
                ? JSON.parse(n.attImageData)
                : undefined,
              tmobileImageDataObject: n.tmobileImageData
                ? JSON.parse(n.tmobileImageData)
                : undefined,
              verizonImageDataObject: n.verizonImageData
                ? JSON.parse(n.verizonImageData)
                : undefined,
              type: n.portStatus === 'externalDirectory' ? 'Directory' : n.type,
              defaultuserName:
                (n as any).defaultuserName ??
                arg.usernameLookup?.[n.defaultuser!] ??
                'N/A',
            };
            row.attImageDataStatus = formatSpam(row.attImageDataObject);
            row.tmobileImageDataStatus = formatSpam(row.tmobileImageDataObject);
            row.verizonImageDataStatus = formatSpam(row.verizonImageDataObject);
            return row;
          }),
        };
      },
      providesTags: ['getBusinessNumbers2'],
    }),
    getCrmCapabilities: builder.query<
      { crmCapabilities: [CrmCapabilities] },
      IQueryProps & {
        crmId: string | number | boolean | null;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/getCrmCapabilities', {
          call: 'getCrmCapabilities',
          crmId: props?.crmId,
        }),
    }),
    addNpsScore: builder.mutation<
      { result: Record<string, never>[] },
      IQueryProps & {
        score: number;
        comment?: string;
        locationName?: string;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/addNpsScore', {
          call: 'addNpsScore',
          score: props.score,
          comment: props.comment ?? '',
          locationName: props.locationName ?? '',
        }),
    }),
    getDisplayNpsForm: builder.query<
      boolean,
      // {success: boolean, result: {userid: number}[]},
      IQueryProps & {
        locationName?: string;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/displayNpsForm', {
          call: 'displayNpsForm',
          locationName: props.locationName,
        }),
      transformResponse(baseQueryReturnValue: {
        result: [{ displayNPS: number }];
      }) {
        return baseQueryReturnValue.result[0].displayNPS === 1;
      },
      providesTags: ['displayNpsForm'],
    }),
    /** Retrieve business with businessDetails and timezone */
    getBusiness2: builder.query<
      businessEntity &
        Omit<timezonesEntity, 'timezoneid'> &
        Pick<businessDetailsEntity, 'allowRingOnHeld'>,
      IQueryProps
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/getBusinessbyBusinessId2', {
          call: 'getBusinessbyBusinessId2',
        }),
      providesTags: ['getBusinessData'],
      // unwrap result
      transformResponse(response: {
        result: businessEntity &
          Omit<timezonesEntity, 'timezoneid'> &
          Pick<businessDetailsEntity, 'allowRingOnHeld'>;
      }) {
        return response.result;
      },
    }),
    /** Update business field */
    updateBusiness: builder.mutation<
      { sucess: boolean; result: Record<string, any> },
      IQueryProps & { name: string; value: string }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/updateBusiness', {
          call: 'updateBusiness',
          name: props.name,
          value: props.value,
        }),
      invalidatesTags: ['getBusinessData'],
    }),
    updateCampaign: builder.mutation<
      { success: boolean; result: Record<string, any> },
      IQueryProps & {
        campaign: Record<string, any>;
      }
    >({
      query: (props) => ({
        url: `${process.env.VITE_APP_CDK_DOMAIN}/utils/campaign/${props.userSettings?.businessId}`,
        method: 'PUT',
        headers: {
          authorization: getCachedAccessToken(),
          'x-api-key': props.userSettings?.apikey,
          'x-ver': version,
          'content-type': 'application/json;charset=utf-8',
        },
        body: {
          userId: props.userSettings?.userid,
          userid: props.userSettings?.userid,
          businessid: props.userSettings?.businessId,
          businessId: props.userSettings?.businessId,
          apikey: props.userSettings?.apikey,
          apiKey: props.userSettings?.apikey,
          campaign: props.campaign,
        },
      }),
    }),
    registerNewCampaign: builder.mutation<
      { success: boolean; result: Record<string, any> },
      IQueryProps & {
        campaign: Record<string, any>;
      }
    >({
      query: (props) => {
        return {
          url: `${process.env.VITE_APP_CDK_DOMAIN}/utils/campaign/${props.userSettings?.businessId}`,
          method: 'POST',
          headers: {
            authorization: getCachedAccessToken(),
            'x-api-key': props.userSettings?.apikey,
            'x-ver': version,
            'content-type': 'application/json;charset=utf-8',
          },
          body: {
            userId: props.userSettings?.userid,
            userid: props.userSettings?.userid,
            businessid: props.userSettings?.businessId,
            businessId: props.userSettings?.businessId,
            apikey: props.userSettings?.apikey,
            apiKey: props.userSettings?.apikey,
            campaign: props.campaign,
          },
        };
      },
    }),

    deletePowerlistContact: builder.mutation<
      { success: boolean },
      IQueryProps & {
        powerlistContactId: number;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'v1/deletePowerlistContact', {
          call: 'deletePowerlistContact',
          powerlistContactId: props.powerlistContactId,
        }),
    }),
    getBusinessProfileBrands: builder.query<
      { success: boolean; result: IBusinessProfileBrand[] },
      IQueryProps
    >({
      query: (props) => {
        return {
          url: `${process.env.VITE_APP_CDK_DOMAIN}/utils/profileBrand/${props.userSettings?.businessId}`,
          method: 'GET',
          headers: {
            authorization: getCachedAccessToken(),
            'x-api-key': props.userSettings?.apikey,
            'x-ver': version,
            'content-type': 'application/json;charset=utf-8',
          },
        };
      },
    }),
    syncProfileBrandData: builder.mutation<{ success: boolean }, IQueryProps>({
      query: (props) => {
        return {
          url: `${process.env.VITE_APP_CDK_DOMAIN}/utils/profileBrand/${props.userSettings?.businessId}/sync`,
          method: 'POST',
          headers: {
            authorization: getCachedAccessToken(),
            'x-api-key': props.userSettings?.apikey,
            'x-ver': version,
            'content-type': 'application/json;charset=utf-8',
          },
          body: {
            businessId: props.userSettings?.businessId,
          },
        };
      },
    }),
    syncBusinessCampaignData: builder.mutation<
      { success: boolean },
      IQueryProps & {
        campaignId: string;
      }
    >({
      query: (props) => {
        return {
          url: `${process.env.VITE_APP_CDK_DOMAIN}/utils/campaign/${props.userSettings?.businessId}/sync`,
          method: 'POST',
          headers: {
            authorization: getCachedAccessToken(),
            'x-api-key': props.userSettings?.apikey,
            'x-ver': version,
            'content-type': 'application/json;charset=utf-8',
          },
          body: {
            businessId: props.userSettings?.businessId,
            campaignId: props.campaignId,
          },
        };
      },
    }),
    getBusinessCampaigns: builder.query<
      { success: boolean; result: IBusinessCampaign[] },
      IQueryProps
    >({
      query: (props) => {
        return {
          url: `${process.env.VITE_APP_CDK_DOMAIN}/utils/campaign/${props.userSettings?.businessId}`,
          method: 'GET',
          headers: {
            authorization: getCachedAccessToken(),
            'x-api-key': props.userSettings?.apikey,
            'x-ver': version,
            'content-type': 'application/json;charset=utf-8',
          },
        };
      },
    }),

    unlinkNumberFromCampaign: builder.mutation<
      { success: boolean },
      IQueryProps & {
        campaignId: string;
        phoneNumbers: string[];
      }
    >({
      query: (props) => {
        return {
          url: `${process.env.VITE_APP_CDK_DOMAIN}/utils/campaign/unlinkNumber`,
          method: 'POST',
          headers: {
            authorization: getCachedAccessToken(),
            'x-api-key': props.userSettings?.apikey,
            'x-ver': version,
            'content-type': 'application/json;charset=utf-8',
          },
          body: {
            businessId: props.userSettings?.businessId,
            campaignId: props.campaignId,
            phoneNumbers: props.phoneNumbers,
          },
        };
      },
    }),
    upsertProfileBrand: builder.mutation<
      { success: boolean; result: Record<string, any> },
      IQueryProps & {
        updatedBrand: Record<string, any>;
      }
    >({
      query: (props) => ({
        url: `${process.env.VITE_APP_CDK_DOMAIN}/utils/profileBrand/${props.userSettings?.businessId}`,
        method: 'PUT',
        headers: {
          authorization: getCachedAccessToken(),
          'x-api-key': props.userSettings?.apikey,
          'x-ver': version,
          'content-type': 'application/json;charset=utf-8',
          Accept: 'application/json', // optional
        },
        body: {
          businessId: props.userSettings?.businessId,
          userId: props.userSettings?.userid,
          updatedBrand: props.updatedBrand,
        },
      }),
    }),
    addProfileBrand: builder.mutation<
      { success: boolean; result: Record<string, any> },
      IQueryProps & {
        updatedBrand: Record<string, any>;
      }
    >({
      query: (props) => ({
        url: `${process.env.VITE_APP_CDK_DOMAIN}/utils/profileBrand/${props.userSettings?.businessId}`,
        method: 'POST',
        headers: {
          authorization: getCachedAccessToken(),
          'x-api-key': props.userSettings?.apikey,
          'x-ver': version,
          'content-type': 'application/json;charset=utf-8',
          Accept: 'application/json', // optional
        },
        body: {
          businessId: props.userSettings?.businessId,
          userId: props.userSettings?.userid,
          updatedBrand: props.updatedBrand,
        },
      }),
    }),

    /** Retrieve business usage report */
    getUsageReportByBusinessId: builder.query<
      IGetUsageReportByBusinessId['result']['hits']['hits'],
      IQueryProps & {
        size: number;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/getUsageReportByBusinessId', {
          call: 'getUsageReportByBusinessId',
          size: props.size,
          bid: props.userSettings?.businessId,
        }),
      transformResponse(response: IGetUsageReportByBusinessId) {
        return response.result?.hits?.hits || [];
      },
    }),
    aioInitializeHubspot: builder.mutation<
      { success: boolean; result: Record<string, unknown> },
      IQueryProps
    >({
      query: (props) => {
        return {
          url: `${process.env.VITE_APP_CDK_DOMAIN}/aio/initialize`,
          method: 'POST',
          headers: {
            authorization: getCachedAccessToken(),
            'x-api-key': props.userSettings?.apikey,
            'x-ver': version,
            'content-type': 'application/json;charset=utf-8',
          },
          body: {
            businessId: props.userSettings?.businessId,
          },
        };
      },
    }),
    aioInitializeWorkflow: builder.mutation<
      { success: boolean; message?: string; error?: string },
      IQueryProps
    >({
      query: (props) => {
        return {
          url: `${process.env.VITE_APP_CDK_DOMAIN}/crm/hubspot/addWorkflow`,
          method: 'POST',
          headers: {
            authorization: getCachedAccessToken(),
            'x-api-key': props.userSettings?.apikey,
            'x-ver': version,
            'content-type': 'application/json;charset=utf-8',
          },
          body: {
            businessId: props.userSettings?.businessId,
          },
        };
      },
    }),
    /** Retrieve business stats for super-admins */
    superAdminBusinessStats: builder.query<
      ISuperAdminBusinessStats,
      IQueryProps
    >({
      query: (props) => ({
        url: '/itn/superAdminBusinessStats/' + props.userSettings?.businessId,
        method: 'GET',
      }),
      // getQueryBody(props, 'superAdminBusinessStats/' + props.userSettings?.businessId, undefined, 'GET'),
      transformResponse(response: ISuperAdminBusinessStats) {
        return response;
      },
    }),

    /** Reverse health check for client */
    healthcheck: builder.mutation<{ rowsAffected: number }, IQueryProps>({
      query: (props) => ({
        url: '/utils/users/healthcheck',
        method: 'POST',
        body: {
          businessId: props.userSettings?.businessId,
          userId: props.userSettings?.userid,
        },
      }),
    }),
    updateUserTerms: builder.mutation<
      {
        success: boolean;
        result: Array<{
          userid: number;
          termsVersionAccepted: string;
          termsAcceptedAt: string;
          termsAcceptedIp: string;
        }>;
      },
      IQueryProps & {
        termsVersionAccepted: string;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'legacy/updateUserTerms', {
          call: 'updateUserTerms',
          termsVersionAccepted: props.termsVersionAccepted,
        }),
      invalidatesTags: ['user'],
    }),
    getOmniDashboardUrl: builder.mutation<
      {
        iframeUrl: string;
        config: Record<string, unknown>;
      },
      IQueryProps & {
        prefersDark?: boolean;
      }
    >({
      query: (props) =>
        getQueryBody(props, 'omnidashboard', {
          prefersDark: props.prefersDark,
        }),
    }),
    addAioCallOutcome: builder.mutation<
      IAddAioCallOutcomeResponse,
      IQueryProps & {
        payload: Record<string, unknown>;
      }
    >({
      query: (props) => ({
        url: 'aio/aioprocessoutcome',
        method: 'POST',
        body: props.payload,
        headers: {
          authorization: getCachedAccessToken(),
          'x-api-key': props.userSettings?.apikey,
          'content-type': 'application/json;charset=utf-8',
        },
      }),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetCallTypesQuery,
  useGetAllBusinessProductsQuery,
  useGetAllBusinessProductsFromDIDQuery,
  useGetBusinessTranchesQuery,
  useGetBusinessQuery,
  useGetDispositionsQuery,
  useGetDirectoryQuery,
  useGetHistoryQuery,
  useUpdateDirectoryMutation,
  useCheckEmailAvailabilityQuery,
  useGetDirectoryTeamsQuery,
  useGetBusinessGroupsQuery,
  useGetVMDropOptionsQuery,
  useGetSMSHistoryQuery,
  useGetUserVoicemailsQuery,
  useGetMCCountQuery,
  useGetVMCountQuery,
  useGetSMSCountQuery,
  useUpdateInstalledVersionMutation,
  useGetUserNumbersAndEndpointsQuery,
  useGetSMSTemplateQuery,
  useUpdateEndpointMutation,
  useGetQueueDirectoryQuery,
  useGetQueueHoldTimeByIdQuery,
  useGetQueueMembersQuery,
  useGetCRMs3Mutation,
  useUpdateNewUsertoOneMutation,
  useUnsubscribeFromAllQueuesByUseridMutation,
  useGetAudioSettingsMutation,
  useUpdateAudioDeviceMutation,
  useGetSipCredsMutation,
  useCheckSipRegistrationMutation,
  useInsertEventMutation,
  useDeleteVoicemailMutation,
  useAddEventMutation,
  useUpdateDevice2Mutation,
  useGetUserDirectoryByBusinessMutation,
  useSubscribeToAutoQueuesMutation,
  useGetPlivoEnvMutation,
  useUpdateUserMutation,
  useSetAsDefaultCRMMutation,
  useRemoveUserCRMMutation,
  useGetQueuesByuseridQuery,
  useGetAlertCenterListQuery,
  useUpdateAlertCenterListMutation,
  useGetCRMReoauthStatusQuery,
  useSetCRMReoauthStatusMutation,
  useUpdateQueueSubscriptionMutation,
  useUpdateC2ConMutation,
  useUpdatePoponMutation,
  useUpdatePresenceonMutation,
  useUpdateStyleModeMutation,
  useUpdateCompactModeMutation,
  useUpdateDefaultDeviceMutation,
  useUpdateUserSettings3Mutation,
  useUpdatePlivoEnvMutation,
  useSendSMSMutation,
  useGetLiveAndHeldCallsByUserQuery,
  useGetSMSTemplateOptionsQuery,
  useGetCallIDQuery,
  useSendToVmDropMutation,
  useC2cMutation,
  useGetThreadedSMSQuery,
  useGetBusinessSMSThreadQuery,
  useGetFeatureFlagsQuery,
  useUpdateQuietModeMutation,
  useGetSwitchUsersByFilterMutation,
  useGetBlockedNumberRulesMutation,
  useGetUserDirectorySubscriptionDataQuery,
  useGetAgentSeatCountNumberCreditQuery,
  useGetUserDirectorySubscriptionDataByUserIdMutation,
  useBulkChangeProductsMutation,
  useUpdateUserProductSettingsMutation,
  useGetActiveUserProductsQuery,
  useUpdateAuditMetaDataMutation,
  useGetAuditMetaDataQuery,
  useGetBusinessNumbersQuery,
  useGetBusinessNumbers2Query,
  useGetCrmCapabilitiesQuery,
  useAddNpsScoreMutation,
  useGetDisplayNpsFormQuery,
  useGetBusiness2Query,
  useUpdateBusinessMutation,
  useUpdateCampaignMutation,
  useRegisterNewCampaignMutation,
  useDeletePowerlistContactMutation,
  useGetUsageReportByBusinessIdQuery,
  useSuperAdminBusinessStatsQuery,
  useHealthcheckMutation,
  useSyncBusinessCampaignDataMutation,
  useSyncProfileBrandDataMutation,
  useUpsertProfileBrandMutation,
  useAddProfileBrandMutation,
  useUpdateUserTermsMutation,
  useGetOmniDashboardUrlMutation,
  useAddAioCallOutcomeMutation,
  useAioInitializeHubspotMutation,
  useAioInitializeWorkflowMutation,
  useGetBusinessProfileBrandsQuery,
  useGetBusinessCampaignsQuery,
  useUnlinkNumberFromCampaignMutation,
} = apigQuery;
